import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import StyledSelect from '../../components/forms/styledSelect';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import FormInput from '../../components/forms/formInput';
import OverlayLoader from '../../components/overlayLoader';
import ServiceCategorySelect from '../../components/selects/serviceCategorySelect';
import { createService, getServicesById, updateService } from '../../services/services';
import { trimProps } from '../../utils/helper';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SbrDrawer from '../../components/sbr/SbrDrawer';

const TagIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
);

const initService = {
    name: '',
    unit: '',
    pricePerUnit: 0,
    serviceCategoryId: '',
    disabled: false
}

const SetServiceModal = ({
    display,
    toggle,
    serviceId,
}) => {
    const { t } = useTranslation();

    const [service, setService] = useState({})

    const queryClient = useQueryClient()
    const { mutate, isLoading } = useMutation(serviceId ? updateService : createService, {
        onSuccess: onMutateSuccess((response, statusCode) => {
            queryClient.invalidateQueries(['services'])
            queryClient.invalidateQueries(['jobs'])
            onHide()
        })
    })

    const { data, isFetching } = useQuery(['services', 'details', serviceId], () => serviceId && getServicesById(serviceId), {
        staleTime: 1000 * 60 * 2,
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            const _service = trimProps(result, initService, ['id'])
            setService(_service)
        })
    }, [data])

    const handleSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()

        mutate(service)
    }

    const onShow = () => {
        !serviceId && setService(initService)
    }

    const onHide = () => {
        toggle(false)
    }

    const handleChange = (objValue) => {
        const _service = { ...service, ...objValue }
        setService(_service)
    }

    return (
        <SbrDrawer
            show={display}
            onHide={onHide}
            onShow={onShow}
            title={serviceId ? t('servicesPage.editServiceTitle') : t('servicesPage.addServiceTitle')}
            subtitle={serviceId ? t('servicesPage.editSubtitle') : t('servicesPage.addSubtitle')}
            icon={<TagIcon />}
            backdrop={isLoading ? 'static' : true}
            footer={
                <>
                    <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={onHide} disabled={isLoading}>{t('common.cancel')}</button>
                    <button type='submit' form="set-service" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isLoading}>
                        {isLoading ? t('servicesPage.submitting') : (serviceId ? t('servicesPage.saveService') : t('servicesPage.addServiceTitle'))}
                    </button>
                </>
            }
        >
            <Form id='set-service' onSubmit={handleSubmit} style={{ position: 'relative' }}>
                <Row className="mb-3">
                    <Col xs="12">
                        <FormInput className="form-control mb-2" label={t('servicesPage.serviceNameLabel')} value={service.name ?? ''}
                            onChange={(e) => handleChange({ name: e.target.value })} required
                        />
                    </Col>
                    <Col lg="6">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('servicesPage.unit')} <span className="text-danger">*</span></Form.Label>
                            <StyledSelect required value={service.unit} onChange={(e) => handleChange({ unit: e.target.value })} >
                                <option value="" disabled>{t('servicesPage.selectUnit')}</option>
                                <option value="m2">m2</option>
                                <option value="kg">kg</option>
                                <option value="piece">piece</option>
                            </StyledSelect>
                        </Form.Group>
                    </Col>
                    <Col lg="6">
                        <FormInput type="number" step="0.01" className="form-control mb-2" label={t('servicesPage.pricePerUnit')} value={service.pricePerUnit ?? ''}
                            onChange={(e) => handleChange({ pricePerUnit: parseFloat(e.target.value) })} required
                        />
                    </Col>
                    <Col lg="12">
                        <Form.Group className="mb-2">
                            <Form.Label>{t('servicesPage.serviceCategory')} <span className="text-danger">*</span></Form.Label>
                            <ServiceCategorySelect required value={service.serviceCategoryId ?? ''} onChange={(option) => handleChange({ serviceCategoryId: option.value })} />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mt-2">
                            <Form.Check label={t('servicesPage.disableService')} checked={service.disabled} value={service.disabled} onChange={(e) => handleChange({ disabled: e.target.checked })} />
                        </Form.Group>
                    </Col>
                </Row>

                {isFetching &&
                    <OverlayLoader />
                }
            </Form>
        </SbrDrawer>
    );
};

export default SetServiceModal;
